// components/SEAHeatmapCard.jsx
import React, { useState } from "react";

/**
 * SEAHeatmapCard (no-CPL variant)
 * - Removes all CPL mentions (badge, table column, tooltips)
 * - Keeps lead volumes and top functions
 */
export default function SEAHeatmapCard({
  title = "SEA Lead Delivery (Pilot Baseline)",
  // no CPL in caption anymore
  totalCaption = "Planned: 220 leads across SEA",
  data = [
    {
      code: "ID",
      name: "Indonesia",
      leads: 44,
      top: ["L&D", "HR"],
      active: true,
    },
    {
      code: "TH",
      name: "Thailand",
      leads: 44,
      top: ["HR", "L&D"],
      active: true,
    },
    {
      code: "PH",
      name: "Philippines",
      leads: 44,
      top: ["HR", "L&D"],
      active: true,
    },
    {
      code: "SG",
      name: "Singapore",
      leads: 44,
      top: ["L&D", "HR"],
      active: true,
    },
    {
      code: "MY",
      name: "Malaysia",
      leads: 44,
      top: ["L&D", "HR"],
      active: true,
    },
  ],
}) {
  // rough schematic positions (unchanged)
  const positions = {
    ID: { x: 58, y: 46 },
    TH: { x: 33, y: 24 },
    PH: { x: 72, y: 24 },
    SG: { x: 48, y: 40 },
    MY: { x: 43, y: 34 },
    VN: { x: 44, y: 22 },
    KH: { x: 38, y: 30 },
    MM: { x: 26, y: 22 },
    LA: { x: 35, y: 22 },
    BN: { x: 53, y: 36 },
    TL: { x: 70, y: 50 },
  };

  const active = data.filter((d) => d.active);
  const maxLeads = Math.max(1, ...active.map((d) => d.leads || 0));

  const colorFor = (v) => {
    const t = Math.max(0, Math.min(1, v / maxLeads));
    const r = Math.round(82 + 100 * t),
      g = Math.round(120 - 40 * t),
      b = Math.round(205 + 30 * t);
    return `rgb(${r},${g},${b})`;
  };

  const [tip, setTip] = useState(null);
  const showTip = (evt, d) => {
    const rect = evt.currentTarget.ownerSVGElement.getBoundingClientRect();
    setTip({
      x: evt.clientX - rect.left + 12,
      y: evt.clientY - rect.top + 12,
      content: d,
    });
  };
  const hideTip = () => setTip(null);

  const total = active.reduce((s, d) => s + (d.leads || 0), 0);

  return (
    <section className="card lg section" aria-label="SEA heatmap">
      <div className="section-head">
        <h3 className="h2">{title}</h3>
        <span className="pill">{totalCaption}</span>
      </div>

      <div className="grid md-g-3" style={{ alignItems: "start" }}>
        {/* Map + legend */}
        <div className="card" style={{ gridColumn: "span 2" }}>
          <div className="xs muted" style={{ marginBottom: 6 }}>
            Shade indicates relative volume among the selected SEA countries.
          </div>

          <div
            style={{
              position: "relative",
              borderRadius: 12,
              overflow: "hidden",
              border: "1px solid var(--line)",
            }}
          >
            <svg
              viewBox="0 0 100 60"
              width="100%"
              height="340"
              role="img"
              aria-label="Schematic map of SEA"
            >
              <rect x="0" y="0" width="100" height="60" fill="#F4F7FE" />
              <ellipse cx="30" cy="25" rx="20" ry="12" fill="#EAF2FF" />
              <ellipse cx="60" cy="44" rx="28" ry="12" fill="#EAF2FF" />
              <ellipse cx="74" cy="24" rx="12" ry="10" fill="#EAF2FF" />

              {data.map((d) => {
                const p = positions[d.code] || { x: 10, y: 10 };
                const size = d.active
                  ? 6 + (10 * (d.leads || 0)) / maxLeads
                  : 5;
                const fill = d.active ? colorFor(d.leads || 0) : "#d1d5db";
                const stroke = d.active ? "#1F5FBF" : "#94a3b8";
                return (
                  <g
                    key={d.code}
                    onMouseMove={(e) => showTip(e, d)}
                    onMouseLeave={hideTip}
                    cursor="pointer"
                  >
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={size}
                      fill={fill}
                      stroke={stroke}
                      strokeWidth="0.8"
                    />
                    <text
                      x={p.x + 1.5}
                      y={p.y - (size + 1.5)}
                      fontSize="3.2"
                      fill="#0B1A3C"
                    >
                      {d.code}
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* Tooltip without CPL */}
            {tip && (
              <div
                style={{
                  position: "absolute",
                  left: tip.x,
                  top: tip.y,
                  background: "#fff",
                  border: "1px solid var(--line)",
                  borderRadius: 10,
                  padding: "8px 10px",
                  boxShadow: "0 10px 24px rgba(2,6,23,.12)",
                  pointerEvents: "none",
                }}
                role="status"
              >
                <div style={{ fontWeight: 700 }}>{tip.content.name}</div>
                <div className="xs muted">
                  Leads: <strong>{tip.content.leads ?? "—"}</strong>
                </div>
                {Array.isArray(tip.content.top) && tip.content.top.length ? (
                  <div className="xs muted">
                    Top functions: {tip.content.top.join(" · ")}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div className="grid g-2" style={{ marginTop: 10 }}>
            <div className="kpi">
              <div className="label">SEA Leads (current)</div>
              <div className="val">{total}</div>
              <div className="xs muted">{totalCaption}</div>
            </div>
            <div className="kpi">
              <div className="label">Legend (low → high)</div>
              <div
                style={{
                  height: 10,
                  borderRadius: 6,
                  overflow: "hidden",
                  background:
                    "linear-gradient(90deg, rgb(82,120,205), rgb(182,80,235))",
                }}
              />
              <div className="xs muted" style={{ marginTop: 6 }}>
                Color reflects relative lead density per country.
              </div>
            </div>
          </div>
        </div>

        {/* Country table — CPL column removed */}
        <div className="card">
          <div className="section-head">
            <h3 className="h2">Country Breakdown</h3>
          </div>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Country</th>
                  <th style={{ width: 90 }}>Leads</th>
                  <th>Top Functions</th>
                </tr>
              </thead>
              <tbody>
                {active.map((d, i) => (
                  <tr key={d.code}>
                    <td className="xs muted">{i + 1}</td>
                    <td className="sm">{d.name}</td>
                    <td className="sm">{d.leads}</td>
                    <td className="xs muted">{(d.top || []).join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
